import React, { useEffect, useState } from "react";

import { Dialog, Button, TextField, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import { getTranslation } from "orion-components/i18n";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Coordinates } from "orion-components/SharedComponents";
import { updateCurrentFeature } from "orion-components/Map/Tools/Actions";

const propTypes = {
	camera: PropTypes.object.isRequired,
	close: PropTypes.func,
	update: PropTypes.func,
	dir: PropTypes.string
};

const CameraDialog = ({ camera, close, update, dir }) => {
	const dispatch = useDispatch();

	const [name, setName] = useState(camera ? camera.entityData.properties.name : "");
	const [description, setDescription] = useState(camera ? camera.entityData.properties.description : "");
	const type = camera ? camera.entityData.properties.deviceType : "";
	const cameraId = camera ? camera.connection.cameraId : "";
	const system = camera ? camera.cameraSystem : "";
	const [error, setError] = useState({
		name: ""
	});
	const dialog = useSelector((state) => camera && (state.appState?.dialog?.openDialog || ""));
	const { feature } = useSelector((state) => state.mapState?.mapTools) || { feature: null };
	const geometry = camera.entityData?.geometry;

	const handleClose = () => {
		close();
	};

	const handleNameChange = (event) => {
		if (event.target.value.length > 50) {
			setError({
				...error,
				name: getTranslation("global.profiles.cameraProfile.cameraDialog.errorText.lessThanFifty")
			});
			return;
		}
		setName(event.target.value);
		setError({ ...error, name: "" });
	};

	const handleDescriptionChange = (event) => {
		setDescription(event.target.value);
	};

	const handleCancel = () => {
		setName(camera ? camera.entityData.properties.name : "");
		setDescription(camera ? camera.entityData.properties.description : "");
		handleClose();
	};

	const handleSave = () => {
		if (!name) {
			setError({
				...error,
				name: getTranslation("global.profiles.cameraProfile.cameraDialog.errorText.noName")
			});
			return;
		}

		const Update = {
			properties: {
				...camera.entityData.properties,
				name: name,
				description: description
			}
		};

		// Only update geometry if coordinates have active changes.
		if (feature?.geometry?.coordinates) {
			Update["geometry"] = {
				...geometry,
				coordinates: feature.geometry.coordinates
			}
		}

		dispatch(update(camera.id, Update));

		handleClose();
	};

	useEffect(() => {
		if (geometry?.coordinates) dispatch(updateCurrentFeature({ geometry }));
		return () => {
			dispatch(updateCurrentFeature(null));
		}
	}, []);

	const handleCoordinatesChange = (coords) => {
		const feature = { geometry: { ...geometry, coordinates: coords } };
		dispatch(updateCurrentFeature(feature));
	}

	const dialogStyles = {
		dialog: {
			maxWidth: 500
		}
	};

	const textFieldStyles = {
		marginBottom: "2rem",
		backgroundColor: "#2C2D2F", // Force styling in Map app
		input: { textOverflow: "ellipsis" }
	};

	const actions = [
		<Button variant="text" style={{ color: "#B2B6BB" }} onClick={handleCancel} key="cancel-action-button" data-testid="camera-edit-cancel-button">
			{getTranslation("global.profiles.cameraProfile.cameraDialog.cancel")}
		</Button>,
		<Button variant="text" color="primary" onClick={handleSave} key="confirm-action-button" data-testid="camera-edit-save-button">
			{getTranslation("global.profiles.cameraProfile.cameraDialog.confirm")}
		</Button>
	];

	return (
		<Dialog
			data-testid="camera-edit-dialog"
			open={dialog === "cameraEditDialog"}
			onRequestClose={handleCancel}
			PaperProps={{ sx: { ...dialogStyles.dialog } }}
		>
			<DialogTitle sx={{ marginBottom: "1.5rem" }} data-testid="orion-components-profiles-camera-profile-components-camera-dialog-dialog-title">
				<Typography sx={{ fontWeight: "400", fontSize: "22px" }}>
					{getTranslation("global.profiles.cameraProfile.cameraDialog.editCam")}
				</Typography>
			</DialogTitle>
			<DialogContent
				sx={{
					paddingTop: "25px",
					paddingBottom: "25px",
					borderTop: "none",
					borderBottom: "none"
				}} data-testid="orion-components-profiles-camera-profile-components-camera-dialog-dialog-content"
			>
				<TextField
					data-testid="camera-name-input"
					id="camera-name"
					fullWidth={true}
					label={getTranslation("global.profiles.cameraProfile.cameraDialog.camName")}
					value={name}
					variant="standard"
					onChange={handleNameChange}
					sx={textFieldStyles}
					errorText={error.name}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
					autoFocus={true}
				/>
				<TextField
					data-testid="camera-description-input"
					id="camera-description"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.cameraProfile.cameraDialog.desc")}
					value={description}
					onChange={handleDescriptionChange}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
				<Coordinates
					disableTargetingIcon={true}
					handleCoordinatesChange={handleCoordinatesChange}
					customStyles={{ margin: 0 }}
					customWidth={220}
				/>
				<TextField
					data-testid="camera-type-input"
					id="camera-type"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.cameraProfile.cameraDialog.type")}
					disabled={true}
					value={type}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
				<TextField
					data-testid="camera-id-input"
					id="camera-id"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.cameraProfile.cameraDialog.camId")}
					disabled={true}
					value={cameraId}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
				<TextField
					data-testid="camera-system-input"
					id="camera-system"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.cameraProfile.cameraDialog.systemName")}
					disabled={true}
					value={system}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
			</DialogContent>
			<DialogActions data-testid="orion-components-profiles-camera-profile-components-camera-dialog-dialog-actions">{actions}</DialogActions>
		</Dialog>
	);
};

CameraDialog.propTypes = propTypes;

export default CameraDialog;
